import {
  ACTIVITY_TYPE_LABELS,
  CALL_OUTCOME_LABELS,
  type ActivityType,
  type CallOutcome,
} from '@marketing-hub/shared';
import type { TimelineItem } from '@/api/types';

export function formatTimelineItem(item: TimelineItem): string {
  if (item.source === 'activity') {
    const kind = item.kind as ActivityType;
    if (kind === 'task') {
      return item.title ?? item.body ?? 'Taak';
    }
    if (kind === 'call' || kind === 'meeting') {
      const outcome = item.payload.call_outcome as CallOutcome | undefined;
      const dur = item.payload.duration_minutes as number | undefined;
      const parts = [item.title ?? ACTIVITY_TYPE_LABELS[kind]];
      if (outcome) parts.push(CALL_OUTCOME_LABELS[outcome] ?? outcome);
      if (dur) parts.push(`${dur} min`);
      return parts.join(' · ');
    }
    return item.body ?? item.title ?? ACTIVITY_TYPE_LABELS[kind] ?? item.kind;
  }

  if (item.kind === 'note' && item.body) return item.body;
  if (
    (item.kind === 'mail_sent' || item.kind === 'mail_campaign_sent') &&
    typeof item.payload.subject === 'string'
  ) {
    return `Verzonden: ${item.payload.subject}`;
  }
  if (item.kind === 'mail_reply' && typeof item.payload.subject === 'string') {
    return `Reply: ${item.payload.subject}`;
  }
  if (item.kind === 'mail_bounce') return 'Bounce ontvangen';
  if (item.kind === 'mail_opened') return 'E-mail geopend (Brevo)';
  if (item.kind === 'mail_clicked') {
    const link = item.payload.link;
    return typeof link === 'string' ? `Link geklikt: ${link}` : 'Link geklikt in e-mail';
  }
  if (item.kind === 'mail_unsubscribed') return 'Uitschrijving (Brevo)';
  if (item.kind === 'mail_send_failed') {
    const code = item.payload.code;
    if (code === 'daily_limit') return 'Verzenden mislukt: daglimiet mailbox';
    if (code === 'hourly_limit') return 'Verzenden mislukt: uurlimiet mailbox';
    return 'Verzenden mislukt';
  }
  if (item.kind === 'status_change') {
    const p = item.payload as { from?: string; to?: string };
    return `Status: ${p.from ?? '?'} → ${p.to ?? '?'}`;
  }
  if (item.kind === 'stage_change') {
    const p = item.payload as { from?: string; to?: string };
    return `Pipeline: ${p.from ?? '?'} → ${p.to ?? '?'}`;
  }
  if (item.kind === 'consent') {
    if (item.payload.action === 'do_not_contact') return 'DNC ingesteld';
  }
  if (item.kind === 'campaign_enrolled') {
    const status = item.payload.status;
    return `Ingeschreven: ${item.title ?? 'campagne'}${status ? ` (${status})` : ''}`;
  }
  return JSON.stringify(item.payload);
}

export function timelineDayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(d);
  day.setHours(0, 0, 0, 0);
  const diff = (today.getTime() - day.getTime()) / 86_400_000;
  if (diff === 0) return 'Vandaag';
  if (diff === 1) return 'Gisteren';
  return d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
}

export function groupTimelineItems(
  items: TimelineItem[],
): { label: string; items: TimelineItem[] }[] {
  const groups: { label: string; items: TimelineItem[] }[] = [];
  let currentLabel = '';
  for (const item of items) {
    const label = timelineDayLabel(item.created_at);
    if (label !== currentLabel) {
      currentLabel = label;
      groups.push({ label, items: [item] });
    } else {
      groups[groups.length - 1]!.items.push(item);
    }
  }
  return groups;
}
